// THE LINK OUT TO THE PUBLISHED PAGE, and the one place this app decides which
// hostname that is.
//
// Every page screen — the listing, the page itself and the editor — states a
// route: `/pricing`, `/es/precios`. A route is not an address, so the one
// question an author holds while looking at any of them ("what does this look
// like to a visitor?") was answered by copying the route, remembering which
// domain the site is on, and typing the two together. This is that, as a link.
//
// WHICH HOSTNAME
//
// A site can have several domains attached and they are not equivalent: a
// `pending` one serves NOTHING — it is a claim, and until the TXT record proves
// control the renderer refuses it exactly as it refuses a hostname nobody has
// attached (see @common/schema/site-domain/status). So a link built from a
// pending domain is a link to somebody else's server or to a certificate error,
// which is worse than no link. Only `active` rows are candidates.
//
// Among several active ones the choice is arbitrary — the API lists them
// through a GSI and the order it returns is not defined — so it is made
// ALPHABETICALLY here rather than left as "whichever arrived first". The value
// of that is not that `a.example` is a better answer than `b.example`; it is
// that the link on this screen is the same link on the next render, in the next
// session and in a screenshot somebody pastes into a bug report.
//
// NOTHING RATHER THAN A DEAD LINK
//
// No active domain draws no link at all, on all three screens. That is the
// ordinary state of a site the day it is created: it is editable, previewable
// and complete, and it is not yet reachable. The screen that has something to
// say about that is Domains, which is one click away in the same site's menu
// and is where attaching and verifying happens; a hint repeated on three page
// screens would be three copies of that screen's job.
import { useQuery } from "@apollo/client";
import _get from "lodash/get";
import { isActive } from "@common/schema/site-domain/status";
import { pageUrl } from "@common/schema/page/url";
import { servedRouteOf } from "@common/schema/page/published";
import * as SiteDomainQL from "queries/site-domain";

// The hostname this site's pages are linked on, out of the rows as they came
// back. Empty string when none of them serves anything yet.
export const liveHostOf = (domains) =>
  ((domains || [])
    .filter(isActive)
    .map((row) => (row && row.host) || "")
    .filter(Boolean)
    .sort())[0] || "";

/**
 * The hostname this site's pages are reachable on, or "".
 *
 * `cache-first` and no error handling, the same call every other cross-cutting
 * read on these screens makes (the site, for its locales; the site's pages, for
 * a translation group). The Domains screen resolves this exact query, so this
 * is a cache read for anybody who has been there, and a site whose domains
 * cannot be listed degrades to the no-domain state above — a screen with one
 * fewer link on it — rather than to an error over a page that is otherwise
 * fine.
 */
export const useLiveHost = (siteId) => {
  const { data } = useQuery(SiteDomainQL.list, {
    variables: { siteId },
    fetchPolicy: "cache-first",
  });

  return liveHostOf(_get(data, "viewer.account.sites.get.domains.list.items"));
};

/**
 * The URL of a page whose route is actually SAVED, or "".
 *
 * The editor is the one screen holding two routes at once — the one the form is
 * editing and the one the row carries — and they are different pages: nothing
 * answers on a route that has not been saved yet. So an edited route is not a
 * link, and it is not a link to the OLD address either, which would be a link
 * whose text and destination name two different pages.
 *
 * The read-only screens hand the same value twice, which is what they have:
 * a listing row and the page screen both show the stored row.
 */
export const liveUrlOf = (host, route, storedRoute) =>
  route === storedRoute ? pageUrl(host, storedRoute) : "";

/**
 * The link itself, or nothing.
 *
 * TO THE SERVED ROUTE, which is not always the row's. A published page is at
 * the route its live version was published with, and a page nothing has
 * published is at no route at all — so the link follows the pointer and draws
 * nothing for a draft, the same rule the renderer applies. A row that predates
 * publishing serves on its own route, as before (see
 * @common/schema/page/published).
 *
 * `target="_blank"`, because the alternative is losing an editor's unsaved
 * form state to a click that reads as "look at this". `rel="noopener"` follows
 * from the target: without it the page opened gets a handle on this window
 * through `window.opener`, and the page opened is a tenant's own markup.
 *
 * The URL is in the title as well as in the href — it is the answer to "which
 * domain is this?", and on the listing the link itself is an icon with no room
 * to say it.
 */
export const LiveLink = ({ host, page, className, children, ...rest }) => {
  const href = pageUrl(host, servedRouteOf(page));

  if (!href) {
    return null;
  }

  return (
    <a
      className={className}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={href}
      {...rest}
    >
      {children}
    </a>
  );
};
